import type { CanvasView, LibraryEntity, LibraryKind, ShotView } from "@dramapie/shared";

import { initialOf, libraryRefUrl } from "./m5";
import { canvasBounds, unplacedRect, type Camera, type Size } from "./m6";

/**
 * 画布补齐（C-01 / C-02 / C-03 / C-05 / C-06 / C-07 / C-08）的纯逻辑。
 *
 * 职责边界：
 * - **服务端仍然是布局真源**：场块 / 镜头卡的初始 rect 直接来自 `CanvasView`，
 *   这里只在其上追加素材带（角色 / 场景 / 道具）与派生带（首帧 / 出片）；
 * - 本地拖动只写 **localStorage sidecar**（`dramapie.canvas.layout.<project>.<episode>`），
 *   绝不改 `shot.order`（C-16：排序是设计预留）；
 * - 连接线 / 影响面都是纯函数，组件只负责渲染，方便单测。
 */

export type CanvasNodeKind = "scene" | "shot" | "asset" | "keyframe" | "clip" | "label";
export type CanvasEdgeKind = "sequence" | "derives" | "references";

/** 没有 `sceneId` 的孤儿镜头统一挂在「未分场」容器里（与服务端 M6 语义一致）。 */
export const UNPLACED_SCENE_ID = "__unplaced__";

export interface CanvasNodeRect {
  x: number;
  y: number;
  w: number;
  h: number;
}

export interface CanvasSceneCard extends CanvasNodeRect {
  id: string;
  kind: "scene";
  sceneId: string;
  /** 服务端场顺序；未分场为 null。 */
  order: number | null;
  name: string;
  time: string;
  shotIds: string[];
}

export interface CanvasShotCard extends CanvasNodeRect {
  id: string;
  kind: "shot";
  sceneId: string;
  shot: ShotView;
}

export interface CanvasAssetCard extends CanvasNodeRect {
  id: string;
  kind: "asset";
  entity: LibraryEntity;
  /** 引用这个素材的镜头数（画布上能连出去的 references 边数）。 */
  refCount: number;
  refShotIds: string[];
}

export interface CanvasDerivedCard extends CanvasNodeRect {
  id: string;
  kind: "keyframe" | "clip";
  shot: ShotView;
}

export interface CanvasBandLabel extends CanvasNodeRect {
  id: string;
  kind: "label";
  text: string;
}

export type CanvasNode =
  | CanvasSceneCard
  | CanvasShotCard
  | CanvasAssetCard
  | CanvasDerivedCard
  | CanvasBandLabel;

export interface CanvasEdge {
  id: string;
  kind: CanvasEdgeKind;
  from: string;
  to: string;
  /** 场与场之间的顺序线：画得更淡（原型 `faint`）。 */
  faint?: boolean;
}

export interface CanvasGraph {
  nodes: CanvasNode[];
  edges: CanvasEdge[];
  /** 所有节点（含素材 / 派生带）的并集，用于 SVG 尺寸。 */
  bounds: CanvasNodeRect;
}

export const shotNodeId = (shotId: string): string => `shot:${shotId}`;
export const sceneNodeId = (sceneId: string): string => `scene:${sceneId}`;
export const assetNodeId = (entityId: string): string => `asset:${entityId}`;
export const keyframeNodeId = (shotId: string): string => `kf:${shotId}`;
export const clipNodeId = (shotId: string): string => `clip:${shotId}`;

// ---------------------------------------------------------------- 素材带

export const CANVAS_ASSET_SIZE: Record<LibraryKind, { w: number; h: number }> = {
  character: { w: 118, h: 150 },
  location: { w: 136, h: 96 },
  prop: { w: 104, h: 78 },
};

export const CANVAS_ASSET_KIND_LABEL: Record<LibraryKind, string> = {
  character: "角色",
  location: "场景",
  prop: "道具",
};

export const CANVAS_ASSET_BAND_LABEL: Record<LibraryKind, string> = {
  character: "角色设定 · CHARACTERS",
  location: "场景 / 全景 · LOCATIONS",
  prop: "道具 · PROPS",
};

const LIBRARY_KIND_ORDER: Record<LibraryKind, number> = { character: 0, location: 1, prop: 2 };
const ASSET_GAP_X = 18;
const ASSET_GAP_Y = 14;
/** 素材带每行最多几张：超过就换行，否则 10 个角色会把画布撑成一条横线。 */
const ASSET_PER_ROW = 6;
const ASSET_ROW_GAP = 30;
const ASSET_LABEL_H = 18;
const BAND_GAP = 48;
const DERIVED_SIZE = { w: 64, h: 92 };
const DERIVED_GAP_X = 8;
const DERIVED_SLOT_GAP = 20;
const DERIVED_ROW_GAP = 16;

// ---------------------------------------------------------------- 本地布局 sidecar

export interface CanvasLayout {
  [nodeId: string]: { x: number; y: number };
}

/** 布局 key 按 project + episode：换集不会串位，也不会上云。 */
export function canvasLayoutKey(projectId: string, episodeId: string): string {
  return `dramapie.canvas.layout.${projectId}.${episodeId}`;
}

function defaultStorage(): Storage | null {
  try {
    if (typeof window === "undefined" || !window.localStorage) return null;
    return window.localStorage;
  } catch {
    // Safari 隐私模式 / 禁用 cookie 时访问 localStorage 会抛。
    return null;
  }
}

function isFinitePoint(value: unknown): value is { x: number; y: number } {
  if (!value || typeof value !== "object") return false;
  const point = value as { x?: unknown; y?: unknown };
  return typeof point.x === "number" && Number.isFinite(point.x) && typeof point.y === "number" && Number.isFinite(point.y);
}

/** 读 sidecar：坏 JSON / 坏形状一律当空布局，不让本地脏数据把画布打崩。 */
export function readCanvasLayout(key: string, storage: Storage | null = defaultStorage()): CanvasLayout {
  if (!storage) return {};
  let raw: string | null = null;
  try {
    raw = storage.getItem(key);
  } catch {
    return {};
  }
  if (!raw) return {};
  try {
    const parsed: unknown = JSON.parse(raw);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) return {};
    const next: CanvasLayout = {};
    for (const [nodeId, value] of Object.entries(parsed as Record<string, unknown>)) {
      if (isFinitePoint(value)) next[nodeId] = { x: value.x, y: value.y };
    }
    return next;
  } catch {
    return {};
  }
}

export function writeCanvasLayout(
  key: string,
  layout: CanvasLayout,
  storage: Storage | null = defaultStorage(),
): void {
  if (!storage) return;
  const payload: CanvasLayout = {};
  for (const [nodeId, value] of Object.entries(layout)) {
    if (isFinitePoint(value)) payload[nodeId] = { x: Math.round(value.x), y: Math.round(value.y) };
  }
  try {
    storage.setItem(key, JSON.stringify(payload));
  } catch {
    // 配额满 / 隐私模式：布局存不下不影响当前会话。
  }
}

export function clearCanvasLayout(key: string, storage: Storage | null = defaultStorage()): void {
  if (!storage) return;
  try {
    storage.removeItem(key);
  } catch {
    // 同上：恢复自动布局是本地行为，失败也不该中断。
  }
}

/** 拖动时把位置写进 sidecar 形状（取整，避免长浮点把 JSON 撑大）。 */
export function moveNodeInLayout(layout: CanvasLayout, nodeId: string, x: number, y: number): CanvasLayout {
  return { ...layout, [nodeId]: { x: Math.round(x), y: Math.round(y) } };
}

// ---------------------------------------------------------------- 引用 / 影响面

/**
 * 素材被镜头引用的判定：
 * - 角色：`shot.chars` 含实体 id，或台词 `who` 等于角色名（与契约 M5 §1 一致）；
 * - 场景：镜头的场名等于实体名（契约里 `ShotView` 没有 location 引用字段）；
 * - 道具：动作 / 台词文本里出现道具名（契约里同样没有结构化引用）。
 *
 * 后两条是**展示层兜底**，不是写回：引用关系永远以服务端字段为准。
 */
export function shotReferencesEntity(args: {
  shot: ShotView;
  sceneName: string | null;
  entity: Pick<LibraryEntity, "kind" | "id" | "name">;
}): boolean {
  const { shot, sceneName, entity } = args;
  const name = entity.name.trim();
  if (entity.kind === "character") {
    if (shot.chars.includes(entity.id)) return true;
    return name.length > 0 && shot.dialogue.some((line) => line.who.trim() === name);
  }
  if (entity.kind === "location") {
    return name.length > 0 && (sceneName ?? "").trim() === name;
  }
  if (name.length === 0) return false;
  const haystack = [shot.action, ...shot.dialogue.map((line) => line.text)].join("\n");
  return haystack.includes(name);
}

export interface CanvasImpactTarget {
  kind: LibraryKind;
  entityId: string;
  name: string;
  /** 触发影响面的素材节点 id（用于高亮来源）。 */
  nodeId: string;
}

export interface CanvasImpactResult {
  target: CanvasImpactTarget;
  shotIds: string[];
  /** 受影响的镜头里已经有关键帧的数量。 */
  keyframeCount: number;
  sceneIds: string[];
  sceneCount: number;
}

function allShotsOf(data: CanvasView): ShotView[] {
  const shots: ShotView[] = [];
  for (const scene of data.scenes) for (const card of scene.shots) shots.push(card.shot);
  for (const card of data.unplaced) shots.push(card.shot);
  return shots;
}

function sceneNameMap(data: CanvasView): Map<string, string> {
  const map = new Map<string, string>();
  for (const scene of data.scenes) map.set(scene.sceneId, scene.name);
  return map;
}

/** 影响面（C-05）：点资产节点后要压暗谁、底部浮条写什么数字，全在这里算。 */
export function computeCanvasImpact(input: {
  data: CanvasView;
  target: CanvasImpactTarget;
}): CanvasImpactResult {
  const { data, target } = input;
  const names = sceneNameMap(data);
  const shots = allShotsOf(data).filter((shot) =>
    shotReferencesEntity({
      shot,
      sceneName: names.get(shot.sceneId) ?? null,
      entity: { kind: target.kind, id: target.entityId, name: target.name },
    }),
  );
  const sceneIds = [...new Set(shots.map((shot) => shot.sceneId))];
  return {
    target,
    shotIds: shots.map((shot) => shot.id),
    keyframeCount: shots.filter((shot) => shot.keyframe !== null).length,
    sceneIds,
    sceneCount: sceneIds.length,
  };
}

/** 选中节点时要点亮的边：只保留与该节点直接相连的（C-04）。 */
export function edgesTouching(edges: readonly CanvasEdge[], nodeIds: ReadonlySet<string>): CanvasEdge[] {
  return edges.filter((edge) => nodeIds.has(edge.from) || nodeIds.has(edge.to));
}

// ---------------------------------------------------------------- 图（节点 + 边）

function compareEntities(a: LibraryEntity, b: LibraryEntity): number {
  const byKind = LIBRARY_KIND_ORDER[a.kind] - LIBRARY_KIND_ORDER[b.kind];
  if (byKind !== 0) return byKind;
  return a.name.localeCompare(b.name, "zh-Hans-CN");
}

function unionRect(rects: readonly CanvasNodeRect[]): CanvasNodeRect {
  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;
  for (const rect of rects) {
    minX = Math.min(minX, rect.x);
    minY = Math.min(minY, rect.y);
    maxX = Math.max(maxX, rect.x + rect.w);
    maxY = Math.max(maxY, rect.y + rect.h);
  }
  if (!Number.isFinite(minX) || !Number.isFinite(minY)) return { x: 0, y: 0, w: 0, h: 0 };
  return { x: minX, y: minY, w: Math.max(0, maxX - minX), h: Math.max(0, maxY - minY) };
}

export interface BuildCanvasGraphInput {
  data: CanvasView;
  /** 资料库实体（`GET /library`）；空数组 = 不画素材带。 */
  library?: readonly LibraryEntity[];
  /** localStorage sidecar 里的本地位置覆盖。 */
  layout?: CanvasLayout;
}

/**
 * 一次算出画布要画的全部节点与边。
 *
 * 节点：场块 / 镜头卡（服务端 rect）+ 素材卡（角色 / 场景 / 道具）+ 派生卡（首帧 / 出片）。
 * 边：`sequence`（镜→镜）、`derives`（镜→首帧→出片）、`references`（素材→镜）。
 */
export function buildCanvasGraph(input: BuildCanvasGraphInput): CanvasGraph {
  const { data, library = [], layout = {} } = input;
  const nodes: CanvasNode[] = [];
  const allShots: ShotView[] = [];
  const sceneNames = sceneNameMap(data);

  data.scenes.forEach((scene, index) => {
    nodes.push({
      id: sceneNodeId(scene.sceneId),
      kind: "scene",
      sceneId: scene.sceneId,
      order: index + 1,
      name: scene.name,
      time: scene.time,
      shotIds: scene.shots.map((card) => card.shotId),
      x: scene.rect.x,
      y: scene.rect.y,
      w: scene.rect.w,
      h: scene.rect.h,
    });
    for (const card of scene.shots) {
      nodes.push({
        id: shotNodeId(card.shotId),
        kind: "shot",
        sceneId: scene.sceneId,
        shot: card.shot,
        x: card.rect.x,
        y: card.rect.y,
        w: card.rect.w,
        h: card.rect.h,
      });
      allShots.push(card.shot);
    }
  });

  const orphan = unplacedRect(data.unplaced);
  if (orphan) {
    nodes.push({
      id: sceneNodeId(UNPLACED_SCENE_ID),
      kind: "scene",
      sceneId: UNPLACED_SCENE_ID,
      order: null,
      name: "未分场",
      time: "",
      shotIds: data.unplaced.map((card) => card.shotId),
      x: orphan.x,
      y: orphan.y,
      w: orphan.w,
      h: orphan.h,
    });
    for (const card of data.unplaced) {
      nodes.push({
        id: shotNodeId(card.shotId),
        kind: "shot",
        sceneId: UNPLACED_SCENE_ID,
        shot: card.shot,
        x: card.rect.x,
        y: card.rect.y,
        w: card.rect.w,
        h: card.rect.h,
      });
      allShots.push(card.shot);
    }
  }

  const sceneArea = canvasBounds(data);
  const bandX = sceneArea?.x ?? 0;
  const scenesBottom = sceneArea ? sceneArea.y + sceneArea.h : 0;

  // 派生带：镜 → 首帧 → 出片（C-03 的 derives 边要有落点）。
  const derivedShots = allShots
    .filter((shot) => shot.keyframe !== null || shot.clip !== null)
    .sort((a, b) => a.order - b.order);
  const derivedColumns = Math.max(1, Math.min(data.columns > 0 ? data.columns : 1, 6));
  const derivedTop = scenesBottom + BAND_GAP;
  const slotW = DERIVED_SIZE.w * 2 + DERIVED_GAP_X;

  if (derivedShots.length > 0) {
    nodes.push({
      id: "label:derived",
      kind: "label",
      text: "首帧 / 出片 · DERIVED",
      x: bandX,
      y: derivedTop - ASSET_LABEL_H - 4,
      w: 260,
      h: ASSET_LABEL_H,
    });
  }
  derivedShots.forEach((shot, index) => {
    const col = index % derivedColumns;
    const row = Math.floor(index / derivedColumns);
    const x = bandX + col * (slotW + DERIVED_SLOT_GAP);
    const y = derivedTop + row * (DERIVED_SIZE.h + DERIVED_ROW_GAP);
    if (shot.keyframe !== null) {
      nodes.push({
        id: keyframeNodeId(shot.id),
        kind: "keyframe",
        shot,
        x,
        y,
        w: DERIVED_SIZE.w,
        h: DERIVED_SIZE.h,
      });
    }
    if (shot.clip !== null) {
      nodes.push({
        id: clipNodeId(shot.id),
        kind: "clip",
        shot,
        x: x + (shot.keyframe !== null ? DERIVED_SIZE.w + DERIVED_GAP_X : 0),
        y,
        w: DERIVED_SIZE.w,
        h: DERIVED_SIZE.h,
      });
    }
  });

  const derivedRows = derivedShots.length > 0 ? Math.ceil(derivedShots.length / derivedColumns) : 0;
  let bandY = derivedShots.length > 0
    ? derivedTop + derivedRows * (DERIVED_SIZE.h + DERIVED_ROW_GAP) + 26
    : derivedTop;

  const entities = [...library].sort(compareEntities);
  const assetNodes: CanvasAssetCard[] = [];
  for (const kind of ["character", "location", "prop"] as const) {
    const items = entities.filter((entity) => entity.kind === kind);
    if (items.length === 0) continue;
    const size = CANVAS_ASSET_SIZE[kind];
    nodes.push({
      id: `label:${kind}`,
      kind: "label",
      text: CANVAS_ASSET_BAND_LABEL[kind],
      x: bandX,
      y: bandY,
      w: 260,
      h: ASSET_LABEL_H,
    });
    const cardsTop = bandY + ASSET_LABEL_H + 4;
    items.forEach((entity, index) => {
      const refShotIds = allShots
        .filter((shot) =>
          shotReferencesEntity({ shot, sceneName: sceneNames.get(shot.sceneId) ?? null, entity }),
        )
        .map((shot) => shot.id);
      const node: CanvasAssetCard = {
        id: assetNodeId(entity.id),
        kind: "asset",
        entity,
        refCount: refShotIds.length,
        refShotIds,
        x: bandX + (index % ASSET_PER_ROW) * (size.w + ASSET_GAP_X),
        y: cardsTop + Math.floor(index / ASSET_PER_ROW) * (size.h + ASSET_GAP_Y),
        w: size.w,
        h: size.h,
      };
      nodes.push(node);
      assetNodes.push(node);
    });
    bandY = cardsTop + size.h + ASSET_ROW_GAP;
  }

  // 本地拖动覆盖：只改坐标，不动服务端 rect / shot.order。
  for (const node of nodes) {
    const override = layout[node.id];
    if (override && Number.isFinite(override.x) && Number.isFinite(override.y)) {
      node.x = override.x;
      node.y = override.y;
    }
  }

  const nodeIds = new Set(nodes.map((node) => node.id));
  const edges: CanvasEdge[] = [];
  const seen = new Set<string>();
  const addEdge = (kind: CanvasEdgeKind, from: string, to: string, faint = false): void => {
    if (!nodeIds.has(from) || !nodeIds.has(to) || from === to) return;
    const id = `${kind}:${from}>${to}`;
    if (seen.has(id)) return;
    seen.add(id);
    edges.push(faint ? { id, kind, from, to, faint: true } : { id, kind, from, to });
  };

  const orderedInScene = data.scenes.map((scene) =>
    [...scene.shots].sort((a, b) => a.shot.order - b.shot.order),
  );
  orderedInScene.forEach((cards) => {
    for (let index = 1; index < cards.length; index += 1) {
      const prev = cards[index - 1];
      const current = cards[index];
      if (prev && current) addEdge("sequence", shotNodeId(prev.shotId), shotNodeId(current.shotId));
    }
  });
  // 场与场之间补一条更淡的顺序线（原型 `faint`）。
  for (let index = 1; index < orderedInScene.length; index += 1) {
    const prev = orderedInScene[index - 1];
    const current = orderedInScene[index];
    const tail = prev?.[prev.length - 1];
    const head = current?.[0];
    if (tail && head) addEdge("sequence", shotNodeId(tail.shotId), shotNodeId(head.shotId), true);
  }
  const orphans = [...data.unplaced].sort((a, b) => a.shot.order - b.shot.order);
  for (let index = 1; index < orphans.length; index += 1) {
    const prev = orphans[index - 1];
    const current = orphans[index];
    if (prev && current) addEdge("sequence", shotNodeId(prev.shotId), shotNodeId(current.shotId));
  }

  for (const shot of [...allShots].sort((a, b) => a.order - b.order)) {
    const kfId = keyframeNodeId(shot.id);
    const clipId = clipNodeId(shot.id);
    const hasKeyframe = nodeIds.has(kfId);
    const hasClip = nodeIds.has(clipId);
    if (hasKeyframe) addEdge("derives", shotNodeId(shot.id), kfId);
    if (hasClip) addEdge("derives", hasKeyframe ? kfId : shotNodeId(shot.id), clipId);
  }

  for (const asset of assetNodes) {
    for (const shotId of asset.refShotIds) addEdge("references", asset.id, shotNodeId(shotId));
  }

  return { nodes, edges, bounds: unionRect(nodes) };
}

// ---------------------------------------------------------------- 适应视口（C-02）

/** 「适应」四周留白：内容包围盒到视口边缘的像素。 */
export const CANVAS_FIT_PADDING = 48;
/** 「适应」缩放上限：不为了填满把卡放大到模糊。 */
export const CANVAS_FIT_MAX_ZOOM = 1;
/** 「适应」缩放下限：再大的板也不缩到看不清。 */
export const CANVAS_FIT_MIN_ZOOM = 0.2;

export interface CanvasFitOptions {
  padding?: number;
  maxZoom?: number;
  minZoom?: number;
}

/**
 * 「适应画面」相机：把**所有节点**（场块 / 镜头卡 / 素材卡 / 派生卡）的并集
 * 居中放进视口，四周留 `padding`。
 *
 * 与 `m6.fitCamera` 的两处关键差别（线上「内容偏下 + 素材带被切」的根因）：
 * - 包围盒由调用方传 `graph.bounds`，而不是只算场块的 `canvasBounds`；
 * - 缩放夹在 0.2–1，且 translate 直接按「内容中心 → 视口中心」求。
 */
export function fitCanvasContent(
  bounds: CanvasNodeRect | null,
  size: Size,
  options: CanvasFitOptions = {},
): Camera | null {
  if (!bounds) return null;
  if (!(size.width > 0) || !(size.height > 0)) return null;
  if (![bounds.x, bounds.y, bounds.w, bounds.h].every((value) => Number.isFinite(value))) return null;

  const padding = options.padding ?? CANVAS_FIT_PADDING;
  const maxZoom = options.maxZoom ?? CANVAS_FIT_MAX_ZOOM;
  const minZoom = options.minZoom ?? CANVAS_FIT_MIN_ZOOM;

  // 0 宽 / 0 高的退化内容按 1px 算，否则除零会把 zoom 变成 Infinity。
  const contentW = Math.max(1, bounds.w);
  const contentH = Math.max(1, bounds.h);
  const usableW = Math.max(1, size.width - padding * 2);
  const usableH = Math.max(1, size.height - padding * 2);
  const raw = Math.min(usableW / contentW, usableH / contentH);
  const zoom = Math.min(maxZoom, Math.max(minZoom, raw));

  return {
    zoom,
    x: size.width / 2 - (bounds.x + bounds.w / 2) * zoom,
    y: size.height / 2 - (bounds.y + bounds.h / 2) * zoom,
  };
}

/** 内容中心落在视口里的哪一点（必须等于视口中心；测试与调试用）。 */
export function contentCenterOnScreen(
  bounds: CanvasNodeRect,
  camera: Camera,
): { x: number; y: number } {
  return {
    x: camera.x + (bounds.x + bounds.w / 2) * camera.zoom,
    y: camera.y + (bounds.y + bounds.h / 2) * camera.zoom,
  };
}

// ---------------------------------------------------------------- 关联快捷视图（C-06）

export type CanvasRelationKind = "character" | "location" | "keyframe" | "clip";

/** 缩略图边长 / 竖向间距 / 距选中节点右边的水平距离。 */
export const CANVAS_RELATION_THUMB = 40;
export const CANVAS_RELATION_GAP = 6;
export const CANVAS_RELATION_OFFSET = 28;

export interface CanvasRelationTarget {
  id: string;
  kind: CanvasRelationKind;
  /** 点击后要 `centerOn` 的目标节点 id。 */
  nodeId: string;
  /** 缩略图地址；null 时画首字占位（不拿 R2 key 拼地址）。 */
  url: string | null;
  /** 无图时的占位字（画面上不出文字，只进 aria-label / title）。 */
  placeholder: string;
  label: string;
}

export interface CanvasRelationItem extends CanvasRelationTarget, CanvasNodeRect {}

export interface CanvasRelationOverlay {
  /** 选中镜头节点 id（虚线起点）。 */
  anchorId: string;
  anchor: CanvasNodeRect;
  /** 缩略图整列的包围盒（用来把 SVG 层扩到装得下它）。 */
  rect: CanvasNodeRect;
  items: CanvasRelationItem[];
}

/**
 * C-06：选中镜头 → 右侧一列半透明缩略图（角色 / 场景 / 关键帧 / 出片）。
 *
 * 只认**画布上真的存在的节点**：没有 keyframe 就没有关键帧缩略图，
 * 一个都凑不出来时返回 null（整块不出现）。纯函数，组件只负责画。
 *
 * `boundsRight` 是内容右边界（world 坐标）：列会溢出板外时翻到节点**左边**
 * （原型 `rightX + size > W → flip`），不让缩略图跑到空白里。
 */
export function relationOverlayFor(input: {
  shot: ShotView | null;
  nodes: readonly CanvasNode[];
  library?: readonly LibraryEntity[];
  boundsRight?: number;
}): CanvasRelationOverlay | null {
  const { shot, nodes, library = [] } = input;
  if (!shot) return null;
  const byId = new Map<string, CanvasNode>(nodes.map((node) => [node.id, node]));
  const anchorNode = byId.get(shotNodeId(shot.id));
  if (!anchorNode || anchorNode.kind !== "shot") return null;

  const targets: CanvasRelationTarget[] = [];
  const push = (
    kind: CanvasRelationKind,
    nodeId: string,
    url: string | null,
    placeholder: string,
    label: string,
  ): void => {
    targets.push({ id: `${kind}:${nodeId}`, kind, nodeId, url, placeholder, label });
  };

  // 1) 角色：shot.chars → 素材带里的 character 卡。
  for (const charId of shot.chars) {
    const node = byId.get(assetNodeId(charId));
    if (!node || node.kind !== "asset" || node.entity.kind !== "character") continue;
    push(
      "character",
      node.id,
      libraryRefUrl(node.entity),
      initialOf(node.entity.name),
      `角色 ${node.entity.name}`,
    );
  }

  // 2) 场景：镜头所属场（场块本身；有同名 location 实体就借它的参考图）。
  const sceneNode = byId.get(sceneNodeId(shot.sceneId));
  if (sceneNode && sceneNode.kind === "scene") {
    const location = library.find(
      (entity) => entity.kind === "location" && entity.name.trim() === sceneNode.name.trim(),
    );
    push(
      "location",
      sceneNode.id,
      location ? libraryRefUrl(location) : null,
      initialOf(sceneNode.name),
      `场景 ${sceneNode.name}`,
    );
  }

  // 3) 关键帧 / 4) 出片：派生带上真有卡才给缩略图。
  const keyframeNode = byId.get(keyframeNodeId(shot.id));
  if (shot.keyframe && keyframeNode && keyframeNode.kind === "keyframe") {
    push("keyframe", keyframeNode.id, shot.keyframe.url, "帧", "首帧");
  }
  const clipNode = byId.get(clipNodeId(shot.id));
  if (shot.clip && clipNode && clipNode.kind === "clip") {
    push("clip", clipNode.id, shot.clip.url, "▷", "出片");
  }

  if (targets.length === 0) return null;

  const anchor: CanvasNodeRect = {
    x: anchorNode.x,
    y: anchorNode.y,
    w: anchorNode.w,
    h: anchorNode.h,
  };
  const rightX = anchor.x + anchor.w + CANVAS_RELATION_OFFSET;
  // 右侧放不下 → 翻到左边（原型同款）；翻过去仍然以节点为轴居中。
  const flipped =
    typeof input.boundsRight === "number" &&
    rightX + CANVAS_RELATION_THUMB > input.boundsRight;
  const columnX = flipped ? anchor.x - CANVAS_RELATION_OFFSET - CANVAS_RELATION_THUMB : rightX;
  const totalH =
    targets.length * CANVAS_RELATION_THUMB + (targets.length - 1) * CANVAS_RELATION_GAP;
  // 竖排以选中节点为轴居中；比节点还高时上下对称溢出，不压住节点。
  const top = anchor.y + (anchor.h - totalH) / 2;
  const items: CanvasRelationItem[] = targets.map((target, index) => ({
    ...target,
    x: columnX,
    y: top + index * (CANVAS_RELATION_THUMB + CANVAS_RELATION_GAP),
    w: CANVAS_RELATION_THUMB,
    h: CANVAS_RELATION_THUMB,
  }));

  return {
    anchorId: anchorNode.id,
    anchor,
    rect: { x: columnX, y: top, w: CANVAS_RELATION_THUMB, h: totalH },
    items,
  };
}

/** 缩略图 → 选中节点的虚线：从靠近缩略图的那条边中点弯出去（左列时从左边出发）。 */
export function relationEdgePath(anchor: CanvasNodeRect, thumb: CanvasNodeRect): string {
  const flipped = thumb.x + thumb.w / 2 < anchor.x + anchor.w / 2;
  const x1 = flipped ? anchor.x : anchor.x + anchor.w;
  const y1 = anchor.y + anchor.h / 2;
  const x2 = flipped ? thumb.x + thumb.w : thumb.x;
  const y2 = thumb.y + thumb.h / 2;
  const offset = Math.max(12, Math.abs(x2 - x1) * 0.6);
  const dir = flipped ? -1 : 1;
  return `M${round(x1)} ${round(y1)} C${round(x1 + dir * offset)} ${round(y1)}, ${round(x2 - dir * offset)} ${round(y2)}, ${round(x2)} ${round(y2)}`;
}

// ---------------------------------------------------------------- 边的几何

function round(value: number): number {
  return Math.round(value * 10) / 10;
}

/**
 * 三次贝塞尔：横向为主时从左右两侧出入，纵向为主时从上下两侧出入。
 * 与原型 `edgeSvg` 同一套锚点规则（素材带在镜头下方，所以纵向锚点必须支持「从下面接」）。
 */
export function edgePath(from: CanvasNodeRect, to: CanvasNodeRect): string {
  const fromCx = from.x + from.w / 2;
  const fromCy = from.y + from.h / 2;
  const toCx = to.x + to.w / 2;
  const toCy = to.y + to.h / 2;
  const dx = toCx - fromCx;
  const dy = toCy - fromCy;

  if (Math.abs(dx) >= Math.abs(dy)) {
    const right = dx >= 0;
    const x1 = right ? from.x + from.w : from.x;
    const y1 = fromCy;
    const x2 = right ? to.x : to.x + to.w;
    const y2 = toCy;
    const offset = Math.max(24, Math.abs(x2 - x1) * 0.45);
    return `M${round(x1)} ${round(y1)} C${round(x1 + (right ? offset : -offset))} ${round(y1)}, ${round(x2 - (right ? offset : -offset))} ${round(y2)}, ${round(x2)} ${round(y2)}`;
  }

  const down = dy >= 0;
  const x1 = fromCx;
  const y1 = down ? from.y + from.h : from.y;
  const x2 = toCx;
  const y2 = down ? to.y : to.y + to.h;
  const offset = Math.max(24, Math.abs(y2 - y1) * 0.45);
  return `M${round(x1)} ${round(y1)} C${round(x1)} ${round(y1 + (down ? offset : -offset))}, ${round(x2)} ${round(y2 - (down ? offset : -offset))}, ${round(x2)} ${round(y2)}`;
}
